'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import {
  ArrowLeft,
  Sliders,
  Clock,
  CalendarCheck,
  ShieldAlert,
  Save,
  CheckCircle2,
  DollarSign
} from 'lucide-react';
import { SettingsSidebar } from '@/components/dashboard/settings/SettingsSidebar';

export default function BookingRulesPage() {
  const [minNoticeHours, setMinNoticeHours] = useState<number>(2); // 2 horas antes
  const [maxAdvanceDays, setMaxAdvanceDays] = useState<number>(30); // 30 días
  const [allowClientCancel, setAllowClientCancel] = useState<boolean>(true);
  const [cancelLimitHours, setCancelLimitHours] = useState<number>(4); // hasta 4 horas antes
  const [slotInterval, setSlotInterval] = useState<number>(30); // 30 min
  const [depositRequired, setDepositRequired] = useState<boolean>(true);
  const [depositAmount, setDepositAmount] = useState<number>(10000);

  const [saving, setSaving] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<{ text: string; type: 'success' | 'error' } | null>(null);

  const fetchRules = useCallback(async () => {
    try {
      const res = await fetch('/api/business');
      const data = await res.json();
      if (data.success && data.business) {
        const b = data.business;
        if (b.slotInterval) setSlotInterval(b.slotInterval);
        if (b.depositRequired !== undefined) setDepositRequired(b.depositRequired);
        if (b.depositAmount) setDepositAmount(b.depositAmount);
        if (b.minNoticeHours !== undefined) setMinNoticeHours(b.minNoticeHours);
        if (b.maxAdvanceDays !== undefined) setMaxAdvanceDays(b.maxAdvanceDays);
        if (b.allowClientCancel !== undefined) setAllowClientCancel(b.allowClientCancel);
        if (b.cancelLimitHours !== undefined) setCancelLimitHours(b.cancelLimitHours);
      }
    } catch (e) {
      console.warn('Error fetching booking rules:', e);
    }
  }, []);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setStatusMessage(null);

    try {
      const res = await fetch('/api/business', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          slotInterval,
          depositRequired,
          depositAmount,
          minNoticeHours,
          maxAdvanceDays,
          allowClientCancel,
          cancelLimitHours,
        })
      });
      const data = await res.json();
      if (data.success) {
        setStatusMessage({ text: 'Reglas de reserva guardadas exitosamente en PostgreSQL.', type: 'success' });
      } else {
        setStatusMessage({ text: data.error || 'Error al guardar reglas.', type: 'error' });
      }
    } catch (err: any) {
      setStatusMessage({ text: err.message || 'Error de conexión', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto pb-16">
      {/* Cabecera */}
      <div className="space-y-1">
        <Link
          href="/dashboard"
          className="inline-flex items-center gap-1 text-xs font-bold text-slate-400 hover:text-slate-600 transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          <span>Volver al inicio</span>
        </Link>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">
          Reglas de reserva
        </h1>
        <p className="text-xs text-slate-500">
          Definí con cuánta anticipación pueden reservar, reprogramar o cancelar tus clientes.
        </p>
      </div>

      {statusMessage && (
        <div
          className={`p-4 rounded-2xl text-xs font-semibold flex items-center justify-between shadow-2xs ${
            statusMessage.type === 'success'
              ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
              : 'bg-rose-50 text-rose-800 border border-rose-200'
          }`}
        >
          <span>{statusMessage.text}</span>
          <button onClick={() => setStatusMessage(null)} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
      )}

      {/* Grid 2 Columnas */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start pt-2">
        {/* Sidebar */}
        <div className="lg:col-span-4">
          <SettingsSidebar currentPath="/dashboard/settings/booking-rules" />
        </div>

        {/* Formulario de Reglas */}
        <form onSubmit={handleSave} className="lg:col-span-8 space-y-6">
          {/* Card 1: Anticipación para reservar */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
            <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
              <div className="w-10 h-10 rounded-2xl bg-emerald-50 text-emerald-700 flex items-center justify-center font-bold">
                <Clock className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Anticipación para reservas</h3>
                <p className="text-xs text-slate-500">Controlá cuándo se abren y cierran los turnos para tus clientes.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Anticipación mínima
                </label>
                <select
                  value={minNoticeHours}
                  onChange={(e) => setMinNoticeHours(Number(e.target.value))}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition"
                >
                  <option value={0}>Sin mínimo (en el momento)</option>
                  <option value={1}>1 hora antes</option>
                  <option value={2}>2 horas antes</option>
                  <option value={4}>4 horas antes</option>
                  <option value={12}>12 horas antes</option>
                  <option value={24}>24 horas antes</option>
                </select>
                <span className="text-[11px] text-slate-400 mt-1 block">
                  Evita que te reserven sobre la hora sin aviso.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Anticipación máxima (ventana de agenda)
                </label>
                <select
                  value={maxAdvanceDays}
                  onChange={(e) => setMaxAdvanceDays(Number(e.target.value))}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition"
                >
                  <option value={7}>Hasta 7 días hacia adelante</option>
                  <option value={14}>Hasta 14 días hacia adelante</option>
                  <option value={30}>Hasta 30 días hacia adelante</option>
                  <option value={60}>Hasta 60 días hacia adelante</option>
                  <option value={90}>Hasta 90 días hacia adelante</option>
                </select>
                <span className="text-[11px] text-slate-400 mt-1 block">
                  Límite de semanas habilitadas en el portal.
                </span>
              </div>
            </div>
          </div>

          {/* Card 2: Cancelaciones y Reprogramaciones */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
            <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
              <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-700 flex items-center justify-center font-bold">
                <ShieldAlert className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Cancelaciones y reprogramaciones</h3>
                <p className="text-xs text-slate-500">Reglas para cuando un cliente no puede asistir.</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
                <div>
                  <h4 className="text-xs font-bold text-slate-900">
                    Permitir cancelación y reprogramación autónoma
                  </h4>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    El cliente puede cancelar o cambiar la fecha desde el enlace en su WhatsApp.
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allowClientCancel}
                    onChange={(e) => setAllowClientCancel(e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
                </label>
              </div>

              {allowClientCancel && (
                <div>
                  <label className="block text-xs font-bold text-slate-700 mb-1.5">
                    Límite para cancelar sin penalización
                  </label>
                  <select
                    value={cancelLimitHours}
                    onChange={(e) => setCancelLimitHours(Number(e.target.value))}
                    className="w-full sm:w-80 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition"
                  >
                    <option value={2}>Hasta 2 horas antes del turno</option>
                    <option value={4}>Hasta 4 horas antes del turno</option>
                    <option value={12}>Hasta 12 horas antes del turno</option>
                    <option value={24}>Hasta 24 horas antes del turno</option>
                    <option value={48}>Hasta 48 horas antes del turno</option>
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* Card 3: Intervalos de Grilla y Señas */}
          <div className="bg-white border border-slate-200/90 rounded-3xl p-6 sm:p-8 shadow-sm space-y-6">
            <div className="flex items-center gap-3 pb-2 border-b border-slate-100">
              <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-700 flex items-center justify-center font-bold">
                <Sliders className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900">Grilla de horarios y señas</h3>
                <p className="text-xs text-slate-500">Configuración técnica de intervalos y depósitos preventivos.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Intervalo entre turnos en la grilla
                </label>
                <select
                  value={slotInterval}
                  onChange={(e) => setSlotInterval(Number(e.target.value))}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition"
                >
                  <option value={15}>Cada 15 minutos</option>
                  <option value={20}>Cada 20 minutos</option>
                  <option value={30}>Cada 30 minutos (recomendado)</option>
                  <option value={45}>Cada 45 minutos</option>
                  <option value={60}>Cada 60 minutos (1 hora)</option>
                </select>
                <span className="text-[11px] text-slate-400 mt-1 block">
                  Define el espaciado de los bloques horarios disponibles.
                </span>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Monto de seña fija por turno
                </label>
                <div className="relative">
                  <span className="absolute left-3 top-2.5 text-xs font-bold text-slate-400">$</span>
                  <input
                    type="number"
                    min={0}
                    step={500}
                    value={depositAmount}
                    onChange={(e) => setDepositAmount(Number(e.target.value))}
                    className="w-full pl-8 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-600 transition"
                  />
                </div>
                <span className="text-[11px] text-slate-400 mt-1 block">
                  Importe que se cobrará vía Mercado Pago para reservar online.
                </span>
              </div>
            </div>

            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl border border-slate-200/80">
              <div>
                <h4 className="text-xs font-bold text-slate-900">
                  Exigir seña por defecto para todos los servicios
                </h4>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  Podés cambiar esto individualmente en cada servicio del catálogo.
                </p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={depositRequired}
                  onChange={(e) => setDepositRequired(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>
          </div>

          {/* Botón Guardar */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="submit"
              disabled={saving}
              className="px-6 py-3 bg-[#0a3826] hover:bg-[#07291c] text-white rounded-2xl text-xs font-bold shadow-md hover:shadow-lg transition inline-flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              <Save className="w-4 h-4 text-emerald-400" />
              <span>{saving ? 'Guardando reglas...' : 'Guardar reglas'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
